/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import {
    Component,
    EventEmitter,
    OnDestroy,
    Output,
    Renderer2,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';

import { combineLatest, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
    RecommendationDialogService,
} from 'ng/modules/logs/services/recommendation-dialog.service';

import * as globalL10n from 'global-l10n';
import * as l10n from './vs-log-cinematic.l10n';

import './vs-log-cinematic.component.less';

import { VsLogsStore } from '../../services';
import { VsLogListStore } from '../vs-log-list/vs-log-list.store';
import { VsLogCinematicStore } from './vs-log-cinematic.store';
import { VsAppProfileType } from '../../vs-logs.types';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const PRINT_WIDTH = 100;
const PRINT_HEADER_WIDTH = 93;
const PRINT_SINGLE_HEADER_WIDTH = 95;

export const enum L4ConnectionType {
    SERVER = 'SERVER',
    CLIENT = 'CLIENT',
}

export const printCss = ` @media print {
    body {
        margin: 0;
        padding: 0;
        width: 100%;
        overflow: hidden !important; /* Prevent horizontal scrolling */
    }

    div[ui-view], .avi-cinematic-portal__backdrop, .root__app-level-alerts,
    avi-skip-link {
        display: none;
    }
}`;

/**
 * @description
 *     Component used for showing a single entry of VS log.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'vs-log-cinematic',
    templateUrl: 'vs-log-cinematic.component.html',
    providers: [VsLogCinematicStore],
})
export class VsLogCinematicComponent implements OnDestroy {
    /**
     * Close the dialog.
     */
    @Output()
    public onClose = new EventEmitter<void>();

    public readonly L4ConnectionType = {
        [L4ConnectionType.SERVER]: L4ConnectionType.SERVER,
        [L4ConnectionType.CLIENT]: L4ConnectionType.CLIENT,
    };

    /**
     * Indicator of whether the log list is being fetched.
     * Useful for the c-view when jumping back and forth a log entry requires page turning.
     */
    public readonly logListIsLoading$ = this.vsLogsStore.tableIsLoading$;

    public readonly listLoadingErrored$ = this.vsLogListStore.hasError$;

    public readonly VsAppProfileType = VsAppProfileType;

    /**
     * Indicator of whether the data is being loaded or errors have occured during the loading by
     * turning two respective observables into one.
     */
    public readonly busy$ = combineLatest([this.logListIsLoading$, this.listLoadingErrored$])
        .pipe(map(([loading, hasError]) => loading || hasError));

    public readonly globalL10nKeys = globalL10nKeys;
    public readonly l10nKeys = l10nKeys;

    public readonly vsName$ = this.vsLogsStore.vsName$;
    public readonly vsAppProfileType$ = this.vsLogsStore.vsAppProfileType$;

    public readonly vsLogListPaginationInfo$ = this.vsLogsStore.vsLogListPaginationInfo$;

    public readonly vsLog$ = this.vsLogCinematicStore.vsLog$;

    public readonly hasPreviousLog$ = this.vsLogCinematicStore.hasPreviousLog$;
    public readonly hasNextLog$ = this.vsLogCinematicStore.hasNextLog$;

    public readonly timestamp$ = this.vsLogCinematicStore.timestamp$;

    public readonly isSignificantLog$ = this.vsLogCinematicStore.isSignificantLog$;

    public readonly significance$ = this.vsLogCinematicStore.significance$;

    public readonly significantLog$ = this.vsLogCinematicStore.significantLog$;

    public readonly wafLog$ = this.vsLogCinematicStore.wafLog$;

    public readonly wafStatus$ = this.vsLogCinematicStore.wafStatus$;

    public readonly ssl$ = this.vsLogCinematicStore.ssl$;

    public readonly applicationInfo$ = this.vsLogCinematicStore.applicationInfo$;

    public readonly grpcInfo$ = this.vsLogCinematicStore.grpcInfo$;

    public readonly httpRequestHeaders$ = this.vsLogCinematicStore.httpRequestHeaders$;

    public readonly httpResponseHeaders$ = this.vsLogCinematicStore.httpResponseHeaders$;

    public readonly hasAuthenticationLogs$ = this.vsLogCinematicStore.hasAuthenticationLogs$;

    public readonly icapLog$ = this.vsLogCinematicStore.icapLog$;

    public readonly botManagementLog$ = this.vsLogCinematicStore.botManagementLog$;

    public readonly datascriptInfo$ = this.vsLogCinematicStore.datascriptInfo$;

    public readonly oobDsRequestLogs$ = this.vsLogCinematicStore.oobDsRequestLogs$;

    public readonly dnsRequestInfo$ = this.vsLogCinematicStore.dnsRequestInfo$;

    public readonly dnsResponseInfo$ = this.vsLogCinematicStore.dnsResponseInfo$;

    public readonly l4ClientConnectionInfo$ = this.vsLogCinematicStore.l4ClientConnectionInfo$;

    public readonly l4ServerConnectionInfo$ = this.vsLogCinematicStore.l4ServerConnectionInfo$;

    private readonly componentDestroySubject$ = new Subject<void>();

    // To store original widths.
    private originalWidths: Map<HTMLElement, string> = new Map();
    private eleColumnLength = 0;

    constructor(
        l10nService: L10nService,
        public readonly vsLogCinematicStore: VsLogCinematicStore,
        private readonly vsLogsStore: VsLogsStore,
        private readonly vsLogListStore: VsLogListStore,
        private readonly recommendationDialogService: RecommendationDialogService,
        private readonly renderer: Renderer2,
    ) {
        l10nService.registerSourceBundles(dictionary);

        vsLogCinematicStore.closeCinematic$.subscribe(() => this.handleClose());
    }

    /**
     * Switch to the next log, triggered by the selection bar.
     * Potentially cause next page load if the last entry of the page has been hit.
     * No guard for checking the last entry is needed here as there's already one from the selection
     * bar.
     */
    public switchToNextLog(): void {
        const { vsLogListPaginationInfo$ } = this.vsLogsStore;
        const logIndex$ = this.vsLogListStore.recentlyViewedLogIndex$;

        combineLatest([
            logIndex$,
            vsLogListPaginationInfo$,
        ])
            .pipe(take(1))
            .subscribe(([currentLogIndex, { page, pageSize }]) => {
                if (currentLogIndex < pageSize - 1) {
                    logIndex$.next(currentLogIndex + 1);
                } else {
                    // this will trigger a +1 page load
                    this.vsLogsStore.setTableCurrentPage(page + 1);
                    logIndex$.next(0);
                }
            });
    }

    /**
     * Switch to the previous log, triggered by the selection bar.
     * Potentially cause previous page load if the first entry of the page has been hit.
     * No guard for checking the first page is needed here as there's already one from the selection
     * bar.
     */
    public switchToPreviousLog(): void {
        const { vsLogListPaginationInfo$ } = this.vsLogsStore;
        const logIndex$ = this.vsLogListStore.recentlyViewedLogIndex$;

        combineLatest([
            logIndex$,
            vsLogListPaginationInfo$,
        ])
            .pipe(take(1))
            .subscribe(([currentLogIndex, { page, pageSize }]) => {
                if (currentLogIndex > 0) {
                    logIndex$.next(currentLogIndex - 1);
                } else {
                    // this will trigger a -1 page load
                    this.vsLogsStore.setTableCurrentPage(page - 1);
                    logIndex$.next(pageSize - 1);
                }
            });
    }

    /**
     * Called when a user exits or closes the view.
     */
    public handleClose(): void {
        this.onClose.emit();
    }

    /**
     * Download the log entry as a .csv file.
     */
    public downloadLog(): void {
        this.vsLog$
            .pipe(take(1))
            .subscribe(({ log_id: logId, service_engine: seId }) => {
                this.vsLogsStore.downloadLogEntry({
                    logId,
                    seId,
                });
            });
    }

    /**
     * Called when the user triggers the print functionality.
     */
    public printLog(): void {
        // Dynamically load the print-specific styles
        this.addPrintStyles();

        // Modify the width of elements
        const elementSelectors = [
            '#ssl .datagrid-column',
            '#ssl .datagrid-cell',
            '.vs-log-cinematic-section-security-bot .datagrid-column',
            '.vs-log-cinematic-section-security-bot .datagrid-cell',
            '#out-of-band .datagrid-column',
            '#out-of-band .datagrid-cell',
        ];

        this.eleColumnLength = 0;

        // Set width for all selected elements
        elementSelectors.forEach(selector => this.setElementWidthForPrinting(selector));

        // Check for specific headers and adjust their width if necessary
        this.adjustHeaderWidthsForPrinting();

        // Trigger the print dialog after making sure styles are applied
        window.print();

        // After printing, reset the widths back to their original values
        this.resetWidthsAfterPrinting();

        // Dynamically remove the print-specific styles
        this.removePrintStyles();
    }

    public openRecommendationDialog(): void {
        combineLatest([
            this.vsLogCinematicStore.applicationLog$,
            this.vsLogsStore.vsUuid$,
        ])
            .pipe(take(1))
            .subscribe(
                ([{ report_timestamp: reportTimestamp, request_id: requestId }, vsUuid]) =>
                    this.recommendationDialogService.openDialog(vsUuid, reportTimestamp, requestId),
            );
    }

    /** @override */
    public ngOnDestroy(): void {
        this.componentDestroySubject$.next();
        this.componentDestroySubject$.complete();

        if (this.recommendationDialogService.isOpen()) {
            this.recommendationDialogService.closeDialog();
        }
    }

    /**
     * Add the print-specific styles to the document by creating a <link> tag.
     * and appending it to the <head> of the document.
     */
    private addPrintStyles(): void {
        const style: HTMLStyleElement = document.createElement('style');

        style.id = 'mediaPrintCss';
        style.innerHTML = printCss;
        document.head.appendChild(style);
    }

    /**
     * Modify the width of elements and store their original width.
     */
    private setElementWidthForPrinting(selector: string): void {
        const elements = document.querySelectorAll(selector);

        this.eleColumnLength = selector.includes('datagrid-column') ? elements.length : this.eleColumnLength;
        elements.forEach((element: HTMLElement) => {
            if (element) {
                // Store the original width
                this.originalWidths.set(element, element.style.width);

                // Use Renderer2 to set the inline width safely
                this.renderer.setStyle(element, 'width', `${PRINT_WIDTH / this.eleColumnLength}%`);
            }
        });
    }

    /**
     * Adjust header width based on content.
     */
    private adjustHeaderWidthsForPrinting(): void {
        const eleSections = document.querySelectorAll('#headers .vs-log-cinematic-sub-section-wrapper');
        // Check whether sections are available.

        if (eleSections.length > 0) {
            // Iterate over the sections to avoid repetition
            eleSections.forEach((section, index) => {
                const eleColumnHeaders = section.querySelectorAll('.avi-diff-grid__column-header');
                const eleDiffColumns = section.querySelectorAll('.avi-diff-grid__diff-column');
                // Apply the header column widths for printing

                this.setHeaderColumnWidthsForPrinting(PRINT_HEADER_WIDTH, eleColumnHeaders, eleDiffColumns);
            });
        }
    }

    /**
     * Reset the widths of elements back to their original values.
     */
    private resetWidthsAfterPrinting(): void {
        this.originalWidths.forEach((originalWidth, element) => {
            this.renderer.setStyle(element, 'width', originalWidth);
        });

        // Clear the map after resetting the widths
        this.originalWidths.clear();
    }

    /**
     * Remove the print-specific styles from the document.
     */
    private removePrintStyles(): void {
        const styleElement = document.getElementById('mediaPrintCss');

        if (styleElement) {
            styleElement.remove();
        }
    }

    /**
     * Set width for column headers and diff columns.
     */
    private setHeaderColumnWidthsForPrinting(width: number, ...elementsLists: Array<NodeListOf<Element>>): void {
        let eleHeaderLength = 0;

        elementsLists.forEach((elements, index) => {
            eleHeaderLength = index === 0 ? elements.length : eleHeaderLength;
            elements.forEach((element: HTMLElement) => {
                this.originalWidths.set(element, element.style.width);

                const calcWidth = `${eleHeaderLength > 1 ? width / eleHeaderLength : PRINT_SINGLE_HEADER_WIDTH}%`;

                this.renderer.setStyle(element, 'width', calcWidth);
            });
        });
    }
}
